import { isResupply } from '@1delta/lender-registry'
import type { TermAdapter } from './index'

/** Resupply stores fee/LTV params as 1e5-scaled integers. */
const e5ToFraction = (v: unknown): number | undefined => {
  if (v == null) return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n / 1e5 : undefined
}

/**
 * Resupply — a CDP minting reUSD against ANOTHER lender's supply position
 * (Curve-Lend / Fraxlend shares, held as Convex-staked ERC-4626).
 *
 * Redemption terms audited in CDP_REDEMPTION_TERMS.md.
 *
 * Three facts the generic sheet cannot know:
 *
 *  1. **A hard per-position borrow floor.** `minimumBorrowAmount` rejects both
 *     a smaller borrow AND a repay that would leave less than it outstanding,
 *     so it bounds partial repayment as well as opening.
 *
 *  2. **Redemptions are COMMUNAL.** Unlike Liquity — where redemption picks
 *     off the lowest-rate trove — Resupply skims collateral pro-rata from
 *     EVERY borrower in the pair. There is no rate to raise and no position to
 *     defend: being well-collateralised does not exempt you, so this is a
 *     term every borrower is exposed to, not a tail risk for the unlucky.
 *
 *  3. **Inherited risk.** The collateral IS a supply position in an external
 *     market, so bad debt there is bad debt here. The wrapped market is named
 *     rather than left as a footnote.
 */
export const resupplyAdapter: TermAdapter = {
  id: 'resupply',
  matches: isResupply,
  profileId: () => 'resupply.cdp@v1',
  build: (input) => {
    const rs = (input.market?.resupply ?? {}) as Record<string, unknown>
    const liquidationFee = e5ToFraction(rs.liquidationFee)
    const mintFee = e5ToFraction(rs.mintFee)
    const minBorrow =
      rs.minimumBorrowAmount != null && String(rs.minimumBorrowAmount) !== '0'
        ? String(rs.minimumBorrowAmount)
        : undefined

    return {
      borrow: {
        debtShape: 'accruing',
        exit: {
          earlyRepay: 'free',
          atMaturityCost: 'accrued',
          lateBehaviour: 'none',
          partialAllowed: true,
          // The floor bounds repayment too: a partial repay may not leave the
          // position under it.
          minDebt: minBorrow,
        },
        liquidation: {
          model: 'repay-seize',
          absorber: 'liquidator',
          trigger: 'price',
          seizure: 'proportional',
          penalty: liquidationFee ?? 0,
          // Every borrower in the pair is exposed, not just the marginal one.
          redeemable: true,
          redemption: {
            trigger: 'permissionless-arbitrage',
            driver: 'below-peg',
            // No queue at all: collateral is skimmed from EVERY borrower in
            // the pair, so unlike Liquity there is nothing to out-run and no
            // rate to raise. Offering "raise your rate" here would be actively
            // misleading.
            order: 'pro-rata',
            // `valueImpact` deliberately ABSENT — it is NOT `usd-neutral`.
            //
            // That was carried over from Liquity, where the redemption fee stays
            // IN the trove as extra collateral and leaves the borrower roughly
            // whole. Resupply does the opposite: `redeemCollateral` takes a 1 %
            // base fee with half of it going to the protocol, and writes the
            // collateral off across the pair via a `WriteOffToken` — so nothing
            // is credited back to the borrower and there is no surplus to claim.
            // Whether they end up neutral is not something we have established,
            // and an absent field says that where `usd-neutral` would have
            // promised it.
            defence:
              'Nothing — redemptions are skimmed pro-rata from every borrower in the pair, so there is no queue to move down and no action that exempts you. Your collateral can shrink with no transaction of your own.',
          },
          badDebt: 'socialized',
        },
        fees:
          mintFee != null && mintFee > 0
            ? [
                {
                  id: 'origination',
                  label: 'Mint fee',
                  when: 'entry',
                  unit: 'percent',
                  basis: 'principal',
                  value: mintFee * 100,
                  payee: 'protocol',
                },
              ]
            : undefined,
        availability: minBorrow ? { minSize: minBorrow } : undefined,
        counterparty: { kind: 'cdp', solvency: 'overcollateralized' },
      },
      supply: {
        // The collateral is itself a lending position elsewhere.
        principal: { protected: false, risks: ['bad-debt'] },
      },
      constraints: {
        crossMargin: false,
        positionModel: 'account',
        positionIdMeaning:
          'One position per address per pair — address-keyed, no discovery.',
      },
    }
  },
}
